/**
 * dashboardSignOffPin.ts
 *
 * Freezing rolling column selections at dashboard sign-off (#968, #963).
 *
 * A rolling rule re-resolves when a new wave lands, so without this a signed
 * dashboard would show different columns with nobody having edited it.
 * Sign-off is the publish event, so it resolves each rule and pins the result.
 * A later wave still lapses the sign-off to needs-re-review as before; the pin
 * holds until someone re-signs, which re-resolves it.
 *
 * This runs on the client because resolving a rule needs the table's column
 * labels, and only the frontend has them without a Carbon round trip from the
 * sign-off path. It walks every page — not just the one on screen — because a
 * partially pinned dashboard is worse than an unpinned one.
 */

import {
  DashboardConfig,
  DashboardLayout,
  DashboardCell,
  TableGraphWidgetData,
} from '../types/gotypes'
import { resolveColumnSelection } from './tableSelection'

/** A chart widget whose rolling rule needs resolving before sign-off. */
export interface PendingPin {
  job: string
  tablePath: string
  widget: TableGraphWidgetData
}

/** Loads the column labels of one table, in table order. */
export type ColumnLoader = (job: string, tablePath: string) => Promise<string[]>

function isRolling(widget: TableGraphWidgetData | undefined): boolean {
  return widget?.selection?.columns?.mode === 'rolling'
}

function walkCells(layout: DashboardLayout | undefined, visit: (cell: DashboardCell) => void): void {
  if (!layout?.rows) return
  layout.rows.forEach(row => {
    (row.cells || []).forEach(cell => {
      visit(cell)
      // Layout widgets nest, so a chart can be several levels down.
      if (cell.widget_type === 'layout' && cell.layout_data) {
        walkCells(cell.layout_data, visit)
      }
      // Report pillars (#1080) hold their entries directly.
      if (cell.widget_type === 'report_pillar' && cell.report_pillar_data) {
        cell.report_pillar_data.entries.forEach(entry => visit(entry.cell))
      }
    })
  })
}

/** Every layout in the dashboard: the single layout, or one per page. */
function allLayouts(config: DashboardConfig): (DashboardLayout | undefined)[] {
  const pages = config.pages || []
  if (pages.length > 0) return pages.map(page => page.layout)
  return [config.layout]
}

/**
 * The chart widgets that need a column rule resolved before this config can be
 * signed. Widgets with a fixed selection, or no selection, do not move on their
 * own and are left alone.
 *
 * Returns the live widget objects, so stamping a pin mutates the config in
 * place — call this on a copy you own.
 */
export function collectPendingPins(config: DashboardConfig): PendingPin[] {
  const pending: PendingPin[] = []
  allLayouts(config).forEach(layout => {
    walkCells(layout, cell => {
      if (cell.widget_type !== 'table_graph') return
      const widget = cell.table_graph_data
      if (!widget || !isRolling(widget)) return
      const tablePath = widget.table_path || ''
      if (!tablePath) return // inline-spec widgets have no table to resolve against
      pending.push({ job: widget.job || '', tablePath, widget })
    })
  })
  return pending
}

/**
 * Returns a copy of the config with every rolling column rule pinned to what it
 * resolves to right now.
 *
 * Rejects if any table's columns cannot be loaded. Signing off with some pins
 * missing would leave part of the dashboard free to move under the signer,
 * which is the exact thing this prevents — better to fail the sign-off and say
 * so.
 */
export async function pinRollingColumnsForSignOff(
  config: DashboardConfig,
  loadColumns: ColumnLoader,
): Promise<DashboardConfig> {
  const copy = JSON.parse(JSON.stringify(config)) as DashboardConfig
  const pending = collectPendingPins(copy)
  if (pending.length === 0) return copy

  // One load per distinct table — a dashboard often charts the same table
  // several ways.
  const byTable = new Map<string, PendingPin[]>()
  pending.forEach(pin => {
    // JSON rather than a joined string: a table path can contain most
    // characters, so any separator risks two different tables colliding.
    const key = JSON.stringify([pin.job, pin.tablePath])
    const group = byTable.get(key)
    if (group) group.push(pin)
    else byTable.set(key, [pin])
  })

  await Promise.all(Array.from(byTable.values()).map(async group => {
    const { job, tablePath } = group[0]
    const allColumns = await loadColumns(job, tablePath)
    group.forEach(({ widget }) => {
      const selection = widget.selection
      if (!selection) return
      const resolved = resolveColumnSelection(selection.columns, allColumns)
      // Pinning every column is the same as pinning nothing, and an empty
      // resolution means the table has no columns to pin.
      if (resolved.length === 0 || resolved.length === allColumns.length) return
      selection.pinned_columns = resolved
    })
  }))

  return copy
}

/**
 * Drops every pin, returning the dashboard to live rolling selections.
 *
 * Used when a sign-off is revoked: an unsigned dashboard has nothing to hold
 * still for. Lapsing to needs-re-review is NOT a revoke — the pin deliberately
 * survives that, so the signed view stays put until someone re-signs.
 */
export function clearPins(config: DashboardConfig): DashboardConfig {
  const copy = JSON.parse(JSON.stringify(config)) as DashboardConfig
  allLayouts(copy).forEach(layout => {
    walkCells(layout, cell => {
      const selection = cell.table_graph_data?.selection
      if (selection?.pinned_columns) delete selection.pinned_columns
    })
  })
  return copy
}
